import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';
import { CreateServiceRequest, Service } from './services.types';
import { environment } from 'environments/environment';
import { SnackbarService } from 'app/shared/services/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class ServicesService {
  private _services = signal<Service[]>([]);
  private _isLoading = signal(false);
  private _isLoaded = signal(false);

  public services = this._services.asReadonly();
  public isLoading = this._isLoading.asReadonly();
  public isLoaded = this._isLoaded.asReadonly();

  constructor(
    private http: HttpClient,
    private snackbarService: SnackbarService
  ) {}

  loadServices(force = false): void {
    if (this._isLoading()) return;
    if (this._isLoaded() && !force) return;

    this._isLoading.set(true);

    this.http
      .get<Service[]>(`${environment.apiUrl}/services`)
      .pipe(
        tap((data) => {
          this._services.set(data);
          this._isLoaded.set(true);
        }),
        catchError((err) => {
          this.snackbarService.showHttpError(err);
          return of(null);
        })
      )
      .subscribe({
        next: () => this._isLoading.set(false),
        error: () => this._isLoading.set(false),
      });
  }

  createService(service: CreateServiceRequest) {
    return this.http.post<Service>(`${environment.apiUrl}/services`, { ...service, isActive: true }).pipe(
      tap((data) => this._services.update((list) => [...list, data])),
      catchError((err) => {
        this.snackbarService.showHttpError(err);
        return of(null);
      })
    );
  }

  updateService(service: Service) {
    return this.http
      .patch<Service>(`${environment.apiUrl}/services/${service.id}`, {
        name: service.name,
        isActive: service.isActive,
      })
      .pipe(
        tap((updated) => this._services.update((list) => list.map((s) => (s.id === updated.id ? updated : s)))),
        catchError((err) => {
          this.snackbarService.showHttpError(err);
          return of(null);
        })
      );
  }

  deleteService(service: Service) {
    return this.http.delete(`${environment.apiUrl}/services/${service.id}`).pipe(
      tap(() => this._services.update((list) => list.filter((s) => s.id !== service.id))),
      catchError((err) => {
        this.snackbarService.showHttpError(err);
        return of(null);
      })
    );
  }
}
